import { datadogRum } from '@datadog/browser-rum';
import type { ObservabilityContext } from '../../observability';

const SITE = 'datadoghq.com';

const CEFSHARP_BRIDGE_ERROR_MESSAGE_PATTERN =
  /^Uncaught "Object Not Found Matching Id:\d+, MethodName:update, ParamCount:\d+"$/;
const EXTENSION_RUNTIME_TAB_NOT_FOUND_ERROR_MESSAGE = 'Invalid call to runtime.sendMessage(). Tab not found.';
const IGNORED_GRAPHQL_ERROR_REASONS = new Set(['SOURCING_LINK_ALREADY_USED', 'CANDIDATE_ALREADY_APPLIED_TO_JOB']);
const IGNORED_FAILED_PRECONDITION_REASONS = new Set(['OTP_EXPIRED']);
const IGNORED_SOURCING_LINK_CONFLICT_MESSAGES = new Set([
  'SOURCING_LINK - SOURCING_LINK_ALREADY_USED',
  'Error: SOURCING_LINK - SOURCING_LINK_ALREADY_USED',
  'SOURCING_LINK - CANDIDATE_ALREADY_APPLIED_TO_JOB',
  'Error: SOURCING_LINK - CANDIDATE_ALREADY_APPLIED_TO_JOB',
]);
const IGNORED_FAILED_PRECONDITION_MESSAGES = new Set([
  'Request failed with status code 412',
  'AxiosError: Request failed with status code 412',
]);
const AUTH0_LOGIN_REQUIRED_MESSAGES = new Set(['Login required', 'Error: Login required']);
const IGNORED_AXIOS_NETWORK_MESSAGES = new Set([
  'Network Error',
  'AxiosError: Network Error',
  'canceled',
  'AxiosError: canceled',
]);

type ErrorInfo = {
  domain?: string;
  reason?: string;
};

type AxiosResponseContext = {
  status?: number;
  data?: {
    details?: ErrorInfo[];
  };
};

type DatadogRumEventContext = {
  endpoint?: string;
  extensions?: {
    errorInfo?: ErrorInfo[];
  };
  response?: AxiosResponseContext;
};

export type DatadogRumEvent = {
  type?: string;
  context?: DatadogRumEventContext;
  error?: {
    message?: string;
    source?: string;
    type?: string;
  };
};

type IgnoredDatadogErrorEvent = {
  name: string;
  matches: (event: DatadogRumEvent) => boolean;
};

export type DatadogServiceConfig = {
  applicationId: string;
  clientToken: string;
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
  /** Must be explicit because the browser default of 100 force-keeps every backend trace and overrides head-sampling rules. */
  traceSampleRate: number;
  service: string; // ex. mloop-employee-website or mloop-candidate-website
  env: 'dev' | 'stage' | 'prod';
  /**
   * Determines how session data is persisted.
   * - 'cookie' (default): Uses browser cookies for session persistence
   * - 'local-storage': Uses localStorage, useful for Chrome extensions or environments where cookies are unavailable
   */
  sessionPersistence?: 'cookie' | 'local-storage';
  /**
   * When enabled, global context and user context are stored in localStorage,
   * allowing them to be preserved across page reloads and synchronized between
   * tabs/windows on the same origin. Requires sessionPersistence: 'local-storage'.
   * Useful for Chrome extensions.
   */
  storeContextsAcrossPages?: boolean;
};

export type DatadogRumInitializer = Pick<typeof datadogRum, 'init'>;

export const isCefSharpBridgeErrorMessage = (message: string): boolean =>
  CEFSHARP_BRIDGE_ERROR_MESSAGE_PATTERN.test(message);

export const isBrowserExtensionRuntimeErrorMessage = (message: string): boolean =>
  message === EXTENSION_RUNTIME_TAB_NOT_FOUND_ERROR_MESSAGE;

/**
 * Browser Reporting API entries (CSP violations, interventions like blocked beforeunload, deprecations) are collected
 * by RUM as report-source errors; they originate from third-party extensions/scripts and browser policy, not our code.
 */
export const isBrowserReport = (event: DatadogRumEvent): boolean => event.error?.source === 'report';

export const isExpectedCandidatePortalGraphqlError = (event: DatadogRumEvent): boolean => {
  const errorInfo = event.context?.extensions?.errorInfo;
  const firstErrorInfo = Array.isArray(errorInfo) ? errorInfo[0] : undefined;

  return (
    firstErrorInfo?.domain === 'SOURCING_LINK' &&
    !!firstErrorInfo.reason &&
    IGNORED_GRAPHQL_ERROR_REASONS.has(firstErrorInfo.reason)
  );
};

export const isExpectedCandidatePortalFailedPrecondition = (event: DatadogRumEvent): boolean => {
  const response = event.context?.response;
  const firstDetail = Array.isArray(response?.data?.details) ? response?.data?.details[0] : undefined;

  return (
    event.context?.endpoint === 'https://api.modernloop.io/cp/verifyOtp' &&
    response?.status === 412 &&
    firstDetail?.domain === 'CANDIDATE_PORTAL' &&
    !!firstDetail.reason &&
    IGNORED_FAILED_PRECONDITION_REASONS.has(firstDetail.reason)
  );
};

export const isExpectedCandidatePortalMessageOnlyError = (message: string): boolean =>
  IGNORED_SOURCING_LINK_CONFLICT_MESSAGES.has(message) || IGNORED_FAILED_PRECONDITION_MESSAGES.has(message);

/**
 * Exact-message allowlist only -- do NOT broaden to substring matching; genuine SSO failures (access_denied,
 * consent_required, invalid_grant, SAML errors) must keep reporting.
 */
export const isExpectedAuth0LoginRequired = (event: DatadogRumEvent): boolean =>
  !!event.error?.message && AUTH0_LOGIN_REQUIRED_MESSAGES.has(event.error.message);

/**
 * Message-scoped on purpose; 'Request failed with status code N' (has a response) and 'timeout of ... exceeded' must
 * keep reporting.
 */
export const isBenignAxiosNetworkError = (message: string): boolean => IGNORED_AXIOS_NETWORK_MESSAGES.has(message);

export const IGNORED_DATADOG_ERROR_EVENTS = [
  {
    name: 'CefSharp bridge object lookup',
    matches: (event) => !!event.error?.message && isCefSharpBridgeErrorMessage(event.error.message),
  },
  {
    name: 'browser extension runtime tab lookup',
    matches: (event) => !!event.error?.message && isBrowserExtensionRuntimeErrorMessage(event.error.message),
  },
  {
    name: 'browser report noise (CSP / interventions / deprecations)',
    matches: isBrowserReport,
  },
  {
    name: 'expected candidate portal sourcing link conflict',
    matches: isExpectedCandidatePortalGraphqlError,
  },
  {
    name: 'expected candidate portal OTP expiration',
    matches: isExpectedCandidatePortalFailedPrecondition,
  },
  {
    name: 'expected candidate portal paired console error',
    matches: (event) => !!event.error?.message && isExpectedCandidatePortalMessageOnlyError(event.error.message),
  },
  {
    name: 'expected Auth0 silent-auth login required',
    matches: isExpectedAuth0LoginRequired,
  },
  {
    name: 'benign axios network/canceled request',
    matches: (event) => !!event.error?.message && isBenignAxiosNetworkError(event.error.message),
  },
] satisfies IgnoredDatadogErrorEvent[];

export const isIgnoredDatadogErrorEvent = (event: DatadogRumEvent): boolean => {
  return IGNORED_DATADOG_ERROR_EVENTS.some(({ matches }) => matches(event));
};

export const shouldSendDatadogEvent = (event: DatadogRumEvent): boolean => {
  if (event.type === 'error' && isIgnoredDatadogErrorEvent(event)) {
    return false;
  }

  return true;
};

/**
 * Cost ceiling for RUM Session Replay.
 *
 * Session Replay is billed per replayed session. In June 2026 it recorded 269 K replay
 * sessions and cost $969/month, which is $3.60 per 1 000 replay sessions. Production
 * replay was then set to 0 and the recorded volume fell to approximately 0.
 *
 * The sample rates come from environment variables, so a single Doppler edit could put
 * that spend back without any review. This ceiling bounds that edit: at the current
 * ~89 K sessions/month, 5 % gives ~4.5 K replay sessions/month, or ~$16/month.
 *
 * To replay more than this, change this constant in a reviewed pull request. Do not
 * raise the environment variable and expect it to take effect.
 */
export const MAX_SESSION_REPLAY_SAMPLE_RATE = 5;

/** Production default. Session Replay stays off unless someone turns it on deliberately. */
export const DEFAULT_SESSION_REPLAY_SAMPLE_RATE = 0;

const clampPercentage = (value: number, max: number): number => {
  if (!Number.isFinite(value) || value < 0) {
    return 0;
  }
  return Math.min(value, max);
};

/**
 * Applies the Session Replay cost ceiling and rejects out-of-range session rates.
 * This is the single place both apps pass through, so the ceiling cannot be bypassed
 * by a per-app configuration change.
 */
export const applyDatadogSamplingCeilings = (config: DatadogServiceConfig): DatadogServiceConfig => ({
  ...config,
  sessionSampleRate: clampPercentage(config.sessionSampleRate, 100),
  sessionReplaySampleRate: clampPercentage(config.sessionReplaySampleRate, MAX_SESSION_REPLAY_SAMPLE_RATE),
});

export const initializeDatadog = async (config: DatadogServiceConfig, rum: DatadogRumInitializer = datadogRum) => {
  try {
    rum.init({
      ...applyDatadogSamplingCeilings(config),
      site: SITE,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'allow',
      allowedTracingUrls: ['https://api.modernloop.io', 'https://api.stg.modernloop.io'],
      // Unsampled requests must carry no Datadog headers so the backend tracer makes its own sampling decision.
      traceContextInjection: 'sampled',
      beforeSend: shouldSendDatadogEvent,
    });
  } catch (e) {
    console.log('Error initializing datadog', e);
  }
};

// There are only two types of users on the FE, customer company employees or candidates
type DatadogUserInfoType = 'employee' | 'candidate';

type DatadogUserInfo = {
  id: string; // This is the user's id in the system employee_id or candidate email
  name: string; // This should be the best name we have for the user
  email: string; // This should be the best email we have for the user
  type: DatadogUserInfoType; // employee or candidate
  org_id: string; // This is the customer company id
  org_name: string; // This is the customer company name
};

export const identifyDatadogUser = async (user: DatadogUserInfo) => {
  try {
    datadogRum.setUser(user);
  } catch (e) {
    console.log('Error identifying user to datadog', e);
  }
};

export const logDatadogError = async (cause: unknown, context?: ObservabilityContext) => {
  try {
    datadogRum.addError(cause, context);
  } catch (e) {
    console.log('Error logging error to datadog', e);
  }
};
